import { useState, useEffect, useRef, useCallback } from "react"
import { Button } from "../components/ui/button"
import { Input } from "../components/ui/input"
import { Textarea } from "../components/ui/textarea"
import { Target, Globe, ArrowRight, Loader2, CheckCircle2 } from "lucide-react"
import { analysisSteps } from "../constant"
import axiosInstance from "../../../API/axiosInstance"
import { toast } from "react-toastify"

export function BrandAnalysis({
    onAnalysisComplete,
    initialIsAnalyzing = false,
    websiteUrl: initialWebsiteUrl = "",
    manualDescription: initialManualDescription = ""
}) {
    const [websiteUrl, setWebsiteUrl] = useState(initialWebsiteUrl)
    const [isAnalyzing, setIsAnalyzing] = useState(initialIsAnalyzing)
    const [analysisStep, setAnalysisStep] = useState(0)
    const [showManualInput, setShowManualInput] = useState(initialManualDescription ? true : false)
    const [manualDescription, setManualDescription] = useState(initialManualDescription)
    const [, setError] = useState(null)
    const [urlError, setUrlError] = useState("")

    const isValidUrl = useCallback((value) => {
        try {
            const url = new URL(value)
            return (url.protocol === "http:" || url.protocol === "https:") && url.hostname.includes(".")
        } catch {
            return false
        }
    }, [])

    const mountedRef = useRef(true)
    const autoStartedRef = useRef(false)
    useEffect(() => {
        mountedRef.current = true
        return () => { mountedRef.current = false }
    }, [])

    const handleAnalyzeBrand = useCallback(async () => {
        // If not already analyzing (manual trigger), check inputs
        if (!initialIsAnalyzing && !websiteUrl && !manualDescription) return

        // Validate URL when using the URL input path
        if (!initialIsAnalyzing && !showManualInput) {
            if (!isValidUrl(websiteUrl)) {
                setUrlError("Please enter a valid URL (e.g. https://yourwebsite.com)")
                return
            }
        }

        setUrlError("")
        setIsAnalyzing(true)
        setAnalysisStep(0)
        setError(null)

        // API Call in parallel
        const apiPromise = axiosInstance.post("/cmo/analyze-brand", {
            websiteUrl: websiteUrl || "https://stripe.com"
        }).then(response => ({ response }), error => ({ error }))

        // Show initial steps for 3 seconds each (except the last one)
        for (let i = 0; i < analysisSteps.length - 1; i++) {
            await new Promise((resolve) => setTimeout(resolve, 3000))
            if (!mountedRef.current) return
            setAnalysisStep(i + 1)
        }

        // Now on the LAST step (analysisStep === analysisSteps.length - 1)
        // Wait for API response if it hasn't finished yet
        try {
            const result = await apiPromise
            if (!mountedRef.current) return
            if (result.error) throw result.error
            const { response } = result

            // All steps complete!
            setAnalysisStep(analysisSteps.length)

            // Short final pause to let the user see the "complete" state
            await new Promise((resolve) => setTimeout(resolve, 1000))
            if (!mountedRef.current) return
            setIsAnalyzing(false)
            onAnalysisComplete(response.data.data)
        } catch (err) {
            if (!mountedRef.current) return
            console.error("Analysis failed:", err)
            const message = err?.response?.data?.message || "Analysis failed. Please check your URL and try again."
            toast.error(message)
            setError(message)
            setIsAnalyzing(false)
        }
    }, [initialIsAnalyzing, websiteUrl, manualDescription, showManualInput, isValidUrl, onAnalysisComplete])

    useEffect(() => {
        if (!initialIsAnalyzing) { autoStartedRef.current = false; return }
        // Strict Mode replays effects; only one analysis request is needed.
        if (autoStartedRef.current) return
        autoStartedRef.current = true
        handleAnalyzeBrand()
    }, [initialIsAnalyzing, handleAnalyzeBrand])

    return (
        <div className="flex-1 min-w-0 flex items-center justify-center p-4 sm:p-6 bg-gray-50 dark:bg-[#0a0a0a]">
            <div className="w-full max-w-xl">
                {!isAnalyzing ? (
                    <div className="space-y-8">
                        <div className="text-center">
                            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-3 bg-orange-50 dark:bg-orange-500/10">
                                <Target className="w-8 h-8 text-orange-500" />
                            </div>
                            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
                                Get your marketing plan
                            </h1>
                            <p className="text-xs font-medium uppercase tracking-wider mt-1.5 mb-4 text-gray-500 dark:text-gray-400">
                                {"We'll map the strategy first, then turn it into creative."}
                            </p>
                            <p className="text-base leading-relaxed text-gray-700 dark:text-gray-400">
                                Your AI CMO creates your strategy, ads, and content based on your brand, audience, and what's working.
                            </p>
                        </div>

                        {!showManualInput ? (
                            <div className="space-y-4">
                                {urlError && (
                                    <p className="flex items-center gap-1.5 text-xs text-red-500 font-medium -mb-2">
                                        <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                        </svg>
                                        {urlError}
                                    </p>
                                )}
                                <div className="relative">
                                    <Globe className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
                                    <Input
                                        type="url"
                                        id="website-url-input"
                                        placeholder="Paste your website — we'll do the rest"
                                        value={websiteUrl}
                                        onChange={(e) => {
                                            const val = e.target.value
                                            setWebsiteUrl(val)
                                            if (!val) {
                                                setUrlError("")
                                            } else if (!isValidUrl(val)) {
                                                setUrlError("Please enter a valid URL (e.g. https://yourwebsite.com)")
                                            } else {
                                                setUrlError("")
                                            }
                                        }}
                                        className={`h-14 pl-12 pr-4 text-base rounded-xl bg-white text-black placeholder:text-gray-400 dark:bg-white/5 dark:text-white dark:placeholder:text-gray-500 ${urlError
                                                ? "border-red-400 dark:border-red-500 focus-visible:border-red-400 focus-visible:ring-red-400/50"
                                                : "border-gray-200 dark:border-white/10"
                                            }`}
                                    />
                                </div>

                                <Button
                                    onClick={handleAnalyzeBrand}
                                    disabled={!websiteUrl || !!urlError}
                                    className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-xl text-base font-medium"
                                >
                                    Get my plan
                                    <ArrowRight className="ml-2 w-5 h-5" />
                                </Button>

                                <button
                                    onClick={() => setShowManualInput(true)}
                                    className="w-full text-center text-sm text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors"
                                >
                                    No website? Describe your product instead
                                </button>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <Textarea
                                    id="manual-description-textarea"
                                    placeholder="Describe your product or service..."
                                    value={manualDescription}
                                    onChange={(e) => setManualDescription(e.target.value)}
                                    rows={5}
                                    className="rounded-xl resize-none text-base bg-white border-gray-200 text-black placeholder:text-gray-400 dark:bg-white/5 dark:border-white/10 dark:text-white dark:placeholder:text-gray-500"
                                />

                                <Button
                                    onClick={handleAnalyzeBrand}
                                    disabled={!manualDescription}
                                    className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-xl text-base font-medium"
                                >
                                    Analyze Brand
                                    <ArrowRight className="ml-2 w-5 h-5" />
                                </Button>

                                <button
                                    onClick={() => setShowManualInput(false)}
                                    className="w-full text-center text-sm text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors"
                                >
                                    Enter website URL instead
                                </button>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="space-y-8">
                        <div className="text-center space-y-3">
                            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-orange-50 dark:bg-orange-500/10">
                                <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
                            </div>
                            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
                                Building your marketing plan
                            </h1>
                        </div>

                        <div className="space-y-3">
                            {analysisSteps.map((step, index) => (
                                <div
                                    key={index}
                                    className={`flex items-center gap-3 p-4 rounded-xl transition-all duration-300 ${index < analysisStep
                                        ? "bg-green-50 border border-green-200 dark:bg-green-500/10 dark:border-green-500/20"
                                        : index === analysisStep
                                            ? "bg-orange-50 border border-orange-200 dark:bg-orange-500/10 dark:border-orange-500/20"
                                            : "bg-gray-50 border border-gray-200 dark:bg-white/5 dark:border-white/10"
                                        }`}
                                >
                                    {index < analysisStep ? (
                                        <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                                    ) : index === analysisStep ? (
                                        <Loader2 className="w-5 h-5 text-orange-500 animate-spin flex-shrink-0" />
                                    ) : (
                                        <div className="w-5 h-5 rounded-full bg-gray-200 dark:bg-white/10" />
                                    )}
                                    <span
                                        className={`text-sm ${index < analysisStep
                                            ? "text-green-600"
                                            : index === analysisStep
                                                ? "text-orange-600"
                                                : "text-gray-400 dark:text-gray-500"
                                            }`}
                                    >
                                        {step}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}
